/**
 * Card — shared neutral card-shell primitive (DASH-F2 / issue #780).
 *
 * Renders one <div data-testid="card"> carrying exactly the four spec'd shell
 * tokens (`--color-surface` background, 0.5px `--color-border` hairline,
 * `--radius-lg` radius, `--shadow-card` shadow) and nothing else — no ember,
 * no accent, no status styling. Pure presentational: no state, no effects,
 * no fetch. Accepts children/slots without imposing card-specific layout;
 * a caller-supplied className is merged after the base classes and all
 * remaining div props are spread through.
 *
 * Consumed by zero existing components in this PR by design (G2 fence):
 * convergence of the drifted card siblings (PrepCenter, ConditionsWidget)
 * is DASH-P1/W1 scope, not here.
 */

// Type alias rather than the plan's literal `interface … extends` form:
// CardProps adds no members beyond the supertype, and the lint baseline
// (@typescript-eslint/no-empty-object-type) rejects the empty-interface
// equivalent. Type-identical contract; recorded in the PR Assumptions.
type CardProps = React.HTMLAttributes<HTMLDivElement>;

const BASE_CLASSES =
  'bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-card)]';

export function Card({ className, children, ...rest }: CardProps) {
  return (
    <div
      data-testid="card"
      className={className ? `${BASE_CLASSES} ${className}` : BASE_CLASSES}
      {...rest}
    >
      {children}
    </div>
  );
}
